/**
 * ReviewModal — Session Complete · Testimonial & Rating
 * 4 Steps: 0=celebrate → 1=rate → 2=testimonial+preview → 3=done+share
 */
import React, { useState, useEffect, useRef } from 'react';
import { getAvatarUrl } from '../data/mockData';
import { useEmail, EMAIL_EVENTS } from '../context/EmailContext';
import { useAuth }                 from '../context/AuthContext';

const MODAL_CSS = `
  @keyframes reviewSlideIn { from{opacity:0;transform:translateY(40px) scale(.96)} to{opacity:1;transform:none} }
  @keyframes confettiFall  { 0%{transform:translateY(-20px) rotate(0);opacity:1} 100%{transform:translateY(140px) rotate(720deg);opacity:0} }
  @keyframes starPop       { 0%{transform:scale(0)} 60%{transform:scale(1.3)} 100%{transform:scale(1)} }
  @keyframes fadeInUp      { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:none} }
  @keyframes pulse         { 0%,100%{transform:scale(1)} 50%{transform:scale(1.06)} }
  @keyframes spin          { to{transform:rotate(360deg)} }
  .rev-overlay  { position:fixed;inset:0;background:rgba(15,15,30,.8);z-index:9999;display:flex;align-items:center;justify-content:center;padding:12px;backdrop-filter:blur(6px) }
  .rev-card     { background:#fff;border-radius:24px;width:100%;max-width:560px;max-height:94vh;overflow-y:auto;box-shadow:0 32px 80px rgba(0,0,0,.32);animation:reviewSlideIn .4s cubic-bezier(.34,1.56,.64,1) }
  .rev-ta:focus { outline:none;border-color:#1DB954 !important;box-shadow:0 0 0 3px rgba(29,185,84,.13) }
  .rev-tag      { padding:6px 13px;border-radius:100px;font-size:.72rem;font-weight:700;cursor:pointer;transition:all .15s;border:1.5px solid #e5e7eb;background:#fff;color:#6b7280;font-family:inherit }
  .rev-tag.sel  { background:#1DB954;border-color:#1DB954;color:#fff }
  .rev-sb       { background:none;border:none;cursor:pointer;padding:2px;line-height:1;transition:all .15s cubic-bezier(.34,1.56,.64,1) }
`;

const STAR_LABELS = ['','Poor 😟','Fair 😐','Good 🙂','Great 😊','Outstanding! 🤩'];
const STAR_COLORS = ['','#ef4444','#f59e0b','#3b82f6','#8b5cf6','#16a34a'];
const CONFETTI    = ['#1DB954','#f59e0b','#8b5cf6','#ef4444','#2563eb','#16a34a','#0891b2','#ec4899'];
const MIN_CHARS   = 30;
const TAGS = ['💡 Clear explanations','🚀 Boosted my confidence','🧠 Deep expertise','⚡ Actionable advice','🤝 Very supportive','📋 Well prepared','🔍 Code review skills','🎯 Focused & on-point','🔄 Will book again'];

function StarRow({ stars, hover, setStars, setHover, size=44 }) {
  return (
    <div style={{display:'flex',gap:4,justifyContent:'center'}}>
      {[1,2,3,4,5].map(n => {
        const on = n<=stars, hv = n<=hover && hover>stars;
        return (
          <button key={n} className="rev-sb"
            style={{fontSize:size,color:on||hv?'#f59e0b':'#e5e7eb',transform:hv?'scale(1.28)':on?'scale(1.08)':'scale(1)',filter:on?'drop-shadow(0 2px 8px rgba(245,158,11,.55))':'none'}}
            onClick={()=>setStars(n)} onMouseEnter={()=>setHover(n)} onMouseLeave={()=>setHover(0)}>★</button>
        );
      })}
    </div>
  );
}

function CatRow({ label, icon, value, onChange }) {
  const [h,setH] = useState(0);
  return (
    <div style={{display:'flex',alignItems:'center',gap:10,padding:'9px 0',borderBottom:'1px solid #f3f4f6'}}>
      <span style={{fontSize:'1.1rem',width:22,textAlign:'center'}}>{icon}</span>
      <span style={{flex:1,fontSize:'.83rem',fontWeight:700,color:'#374151'}}>{label}</span>
      <div style={{display:'flex',gap:1}}>
        {[1,2,3,4,5].map(n=>(
          <button key={n} className="rev-sb"
            style={{fontSize:18,color:n<=value||(n<=h&&h>value)?'#f59e0b':'#e5e7eb',transform:n<=h&&h>value?'scale(1.2)':'scale(1)'}}
            onClick={()=>onChange(n)} onMouseEnter={()=>setH(n)} onMouseLeave={()=>setH(0)}>★</button>
        ))}
      </div>
      <span style={{width:26,fontSize:'.72rem',color:value?'#f59e0b':'#d1d5db',fontWeight:800,textAlign:'right'}}>{value?value+'.0':'—'}</span>
    </div>
  );
}

function ProgressBar({ step }) {
  return (
    <div style={{display:'flex',gap:5}}>
      {[0,1,2].map(i=>(
        <div key={i} style={{flex:1,height:3,borderRadius:100,background:i<=step?'rgba(255,255,255,.9)':'rgba(255,255,255,.2)',transition:'background .3s'}}/>
      ))}
    </div>
  );
}

function PreviewCard({ session, stars, testimonial, tags, isPublic, user }) {
  const name = isPublic ? (user?.name||'You') : 'Anonymous Mentee';
  const init = isPublic ? (user?.initials||'?') : '?';
  const bg   = user?.avatarBg||'2563eb';
  return (
    <div style={{background:'#fff',border:'1.5px solid #e5e7eb',borderRadius:14,padding:'16px',boxShadow:'0 4px 16px rgba(0,0,0,.06)',animation:'fadeInUp .3s ease'}}>
      <div style={{display:'flex',gap:2,marginBottom:8}}>
        {[1,2,3,4,5].map(n=>(
          <span key={n} style={{fontSize:15,color:n<=stars?'#f59e0b':'#e5e7eb',filter:n<=stars?'drop-shadow(0 1px 3px rgba(245,158,11,.4))':'none'}}>★</span>
        ))}
        {stars>0 && <span style={{marginLeft:6,fontSize:'.7rem',fontWeight:800,color:STAR_COLORS[stars],alignSelf:'center'}}>{STAR_LABELS[stars]}</span>}
      </div>
      <p style={{fontSize:'.84rem',color:testimonial?'#374151':'#d1d5db',lineHeight:1.75,margin:'0 0 10px',fontStyle:'italic',borderLeft:'3px solid #1DB954',paddingLeft:10,minHeight:36}}>
        "{testimonial||'Your testimonial will appear here…'}"
      </p>
      {tags.length>0&&(
        <div style={{display:'flex',flexWrap:'wrap',gap:4,marginBottom:10}}>
          {tags.map(t=>(
            <span key={t} style={{padding:'2px 8px',borderRadius:100,background:'#f0fdf4',color:'#16a34a',fontSize:'.63rem',fontWeight:700}}>{t}</span>
          ))}
        </div>
      )}
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <div style={{width:28,height:28,borderRadius:'50%',background:`#${bg}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'.62rem',fontWeight:900,color:'#fff',flexShrink:0}}>{init}</div>
        <div>
          <div style={{fontSize:'.77rem',fontWeight:800,color:'#111827'}}>{name}</div>
          <div style={{fontSize:'.65rem',color:'#9ca3af'}}>{session.type} · {session.date}</div>
        </div>
        <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:3}}>
          <div style={{width:16,height:16,borderRadius:'50%',background:'#1DB954',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'.5rem',color:'#fff'}}>✓</div>
          <span style={{fontSize:'.6rem',color:'#16a34a',fontWeight:700}}>Verified</span>
        </div>
      </div>
    </div>
  );
}

export default function ReviewModal({ session, onSubmit, onClose }) {
  const { sendEmail } = useEmail();
  const { user }      = useAuth();
  const taRef         = useRef(null);

  const [step,       setStep]       = useState(0);
  const [stars,      setStars]      = useState(0);
  const [hov,        setHov]        = useState(0);
  const [cats,       setCats]       = useState({communication:0,knowledge:0,punctuality:0,helpfulness:0});
  const [text,       setText]       = useState('');
  const [tags,       setTags]       = useState([]);
  const [pub,        setPub]        = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const activeStar = hov||stars;
  const canNext    = stars>0;
  const canSubmit  = stars>0 && text.trim().length>=MIN_CHARS;

  useEffect(()=>{ if(step===2) setTimeout(()=>taRef.current?.focus(),120); },[step]);

  const toggleTag = t => setTags(p=>p.includes(t)?p.filter(x=>x!==t):[...p,t]);

  const handleSubmit = async () => {
    if(!canSubmit) return;
    setSubmitting(true);
    await new Promise(r=>setTimeout(r,700));
    sendEmail(EMAIL_EVENTS.SESSION_COMPLETED,{
      menteeName: user?.name||'Mentee', menteeEmail: user?.email||'mentee@devbrains.com',
      mentorName: session.mentor, sessionDate: session.date,
      sessionType: session.type,
      reviewUrl: `${window.location.origin}/mentors`,
      platformUrl: 'www.devbrains.in',
    });
    setSubmitting(false);
    setStep(3);
    if(onSubmit) onSubmit({
      sessionId:session.id, mentorName:session.mentor,
      stars, catRatings:cats, testimonial:text.trim(),
      tags, isPublic:pub, reviewer:pub?user?.name:'Anonymous',
      submittedAt:new Date().toISOString(),
    });
  };

  const liUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin+'/mentors')}&summary=${encodeURIComponent(`Just completed a ${session.type} session with ${session.mentor} on DevBrains! ⭐ ${stars}/5`)}`;
  const twUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Just had a ${stars}⭐ session with ${session.mentor} on @DevBrains! ${STAR_LABELS[stars]?.split(' ')[0]||''} devbrains.in`)}`;

  return (
    <div className="rev-overlay" onClick={step===3?onClose:undefined}>
      <style>{MODAL_CSS}</style>
      <div className="rev-card" onClick={e=>e.stopPropagation()}>

        {/* ─── STEP 0: CELEBRATION ─────────────────── */}
        {step===0&&(
          <div>
            <div style={{background:'linear-gradient(135deg,#1DB954,#16a34a)',padding:'32px 28px',textAlign:'center',position:'relative',overflow:'hidden'}}>
              {[...Array(8)].map((_,i)=>(
                <div key={i} style={{position:'absolute',top:`${10+i*11}%`,left:`${5+i*12}%`,fontSize:['.9rem','.6rem','1.1rem','.7rem','.8rem','.55rem','1rem','.65rem'][i],opacity:.2,animation:`pulse ${1.5+i*.2}s ease-in-out ${i*.15}s infinite`}}>✦</div>
              ))}
              <div style={{fontSize:64,lineHeight:1,marginBottom:12,animation:'starPop .5s cubic-bezier(.34,1.56,.64,1) .1s both'}}>🎉</div>
              <h2 style={{color:'#fff',fontWeight:900,fontSize:'1.5rem',margin:'0 0 6px',letterSpacing:'-.4px'}}>Session Complete!</h2>
              <p style={{color:'rgba(255,255,255,.85)',fontSize:'.88rem',margin:0}}>
                Great work, <strong>{user?.name?.split(' ')[0]||'you'}!</strong>
              </p>
            </div>

            <div style={{padding:'24px 28px'}}>
              {/* Session summary */}
              <div style={{background:'#f8fafc',border:'1px solid #e5e7eb',borderRadius:14,padding:'16px',marginBottom:20}}>
                <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:14}}>
                  <img src={getAvatarUrl(session.mentorAvatar||session.mentor,session.mentorBg||'16a34a',46)}
                    alt={session.mentor}
                    onError={e=>{e.target.style.display='none';e.target.nextSibling.style.display='flex';}}
                    style={{width:46,height:46,borderRadius:'50%',border:'2px solid #1DB954'}}/>
                  <div style={{width:46,height:46,borderRadius:'50%',background:`#${session.mentorBg||'16a34a'}`,display:'none',alignItems:'center',justifyContent:'center',fontSize:'.9rem',fontWeight:900,color:'#fff',border:'2px solid #1DB954'}}>
                    {session.mentorInit}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:900,fontSize:'.95rem',color:'#111827'}}>{session.mentor}</div>
                    <div style={{fontSize:'.73rem',color:'#6b7280'}}>{session.type}</div>
                  </div>
                  <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:8,padding:'5px 10px',textAlign:'center'}}>
                    <div style={{fontSize:'.6rem',fontWeight:800,color:'#16a34a',textTransform:'uppercase',letterSpacing:'.05em'}}>✅ Done</div>
                  </div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
                  {[['📅','Date',session.date],['⏱','Duration',session.duration||'60 min'],['💬','Type',session.type]].map(([ic,lbl,val])=>(
                    <div key={lbl} style={{background:'#fff',borderRadius:9,padding:'9px',textAlign:'center',border:'1px solid #f3f4f6'}}>
                      <div style={{fontSize:'1.1rem',marginBottom:2}}>{ic}</div>
                      <div style={{fontSize:'.6rem',fontWeight:700,color:'#9ca3af',textTransform:'uppercase',letterSpacing:'.05em'}}>{lbl}</div>
                      <div style={{fontSize:'.76rem',fontWeight:800,color:'#374151',marginTop:2}}>{val}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{background:'linear-gradient(135deg,#fffbeb,#fef3c7)',border:'1px solid #fde68a',borderRadius:12,padding:'13px 16px',marginBottom:20}}>
                <div style={{fontWeight:800,color:'#92400e',fontSize:'.83rem',marginBottom:3}}>⭐ Help {session.mentor} & future learners!</div>
                <div style={{fontSize:'.77rem',color:'#b45309',lineHeight:1.6}}>
                  Share your experience to help others find the right mentor. Takes only 60 seconds.
                </div>
              </div>

              <div style={{display:'flex',gap:10}}>
                <button onClick={()=>setStep(1)} style={{flex:1,padding:'13px',borderRadius:12,border:'none',background:'#1DB954',color:'#fff',fontFamily:'inherit',fontWeight:900,fontSize:'.9rem',cursor:'pointer'}}>
                  ⭐ Rate &amp; Review →
                </button>
                <button onClick={onClose} style={{padding:'13px 16px',borderRadius:12,border:'1.5px solid #e5e7eb',background:'#fff',fontFamily:'inherit',color:'#9ca3af',cursor:'pointer',fontWeight:600}}>
                  Later
                </button>
              </div>
              <p style={{textAlign:'center',fontSize:'.67rem',color:'#d1d5db',marginTop:8}}>
                Rate any time from Past Sessions in your dashboard
              </p>
            </div>
          </div>
        )}

        {/* ─── STEP 1: STAR RATING ─────────────────── */}
        {step===1&&(
          <div>
            <div style={{background:'linear-gradient(135deg,#1a1a2e,#16213e)',padding:'24px 28px'}}>
              <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:14}}>
                <div style={{position:'relative'}}>
                  <img src={getAvatarUrl(session.mentorAvatar||session.mentor,session.mentorBg||'16a34a',48)}
                    alt={session.mentor}
                    onError={e=>{e.target.style.display='none';e.target.nextSibling.style.display='flex';}}
                    style={{width:48,height:48,borderRadius:'50%',border:'2.5px solid #1DB954'}}/>
                  <div style={{width:48,height:48,borderRadius:'50%',background:`#${session.mentorBg||'16a34a'}`,display:'none',alignItems:'center',justifyContent:'center',fontSize:'1rem',fontWeight:900,color:'#fff',border:'2.5px solid #1DB954'}}>
                    {session.mentorInit}
                  </div>
                  <div style={{position:'absolute',bottom:-2,right:-2,background:'#1DB954',borderRadius:'50%',width:17,height:17,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'.55rem'}}>✅</div>
                </div>
                <div>
                  <div style={{color:'rgba(255,255,255,.6)',fontSize:'.63rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:2}}>Step 1 of 2 · Rate Your Session</div>
                  <div style={{color:'#fff',fontWeight:900,fontSize:'1rem'}}>{session.mentor}</div>
                  <div style={{color:'rgba(255,255,255,.5)',fontSize:'.72rem'}}>{session.type} · {session.date}</div>
                </div>
              </div>
              <ProgressBar step={0}/>
            </div>

            <div style={{padding:'26px 28px'}}>
              {/* Overall stars */}
              <div style={{textAlign:'center',marginBottom:22}}>
                <div style={{fontSize:'.7rem',fontWeight:700,color:'#9ca3af',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:13}}>Overall Experience</div>
                <StarRow stars={stars} hover={hov} setStars={setStars} setHover={setHov} size={46}/>
                <div style={{height:26,display:'flex',alignItems:'center',justifyContent:'center',marginTop:8}}>
                  {activeStar>0
                    ? <div style={{fontSize:'1rem',fontWeight:800,color:STAR_COLORS[activeStar],animation:'fadeInUp .2s ease'}}>{STAR_LABELS[activeStar]}</div>
                    : <div style={{fontSize:'.83rem',color:'#d1d5db'}}>Tap a star to rate</div>}
                </div>
              </div>

              {/* Category ratings */}
              <div style={{background:'#f9fafb',borderRadius:12,padding:'2px 16px 2px',marginBottom:22}}>
                <div style={{fontSize:'.62rem',fontWeight:800,textTransform:'uppercase',letterSpacing:'.07em',color:'#9ca3af',padding:'10px 0 5px'}}>Rate by category (optional)</div>
                <CatRow label="Communication"       icon="💬" value={cats.communication} onChange={v=>setCats(p=>({...p,communication:v}))}/>
                <CatRow label="Technical Knowledge" icon="🧠" value={cats.knowledge}     onChange={v=>setCats(p=>({...p,knowledge:v}))}/>
                <CatRow label="Punctuality"         icon="⏰" value={cats.punctuality}   onChange={v=>setCats(p=>({...p,punctuality:v}))}/>
                <CatRow label="Helpfulness"         icon="🤝" value={cats.helpfulness||0} onChange={v=>setCats(p=>({...p,helpfulness:v}))}/>
              </div>

              <div style={{display:'flex',gap:10}}>
                <button onClick={()=>setStep(0)} style={{padding:'13px 16px',borderRadius:12,border:'1.5px solid #e5e7eb',background:'#fff',fontFamily:'inherit',color:'#6b7280',cursor:'pointer',fontWeight:600}}>←</button>
                <button onClick={()=>canNext&&setStep(2)} disabled={!canNext}
                  style={{flex:1,padding:'13px',borderRadius:12,border:'none',fontFamily:'inherit',fontWeight:900,fontSize:'.9rem',cursor:canNext?'pointer':'not-allowed',background:canNext?'#1DB954':'#e5e7eb',color:canNext?'#fff':'#9ca3af',transition:'all .2s'}}>
                  Continue → Write Testimonial
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── STEP 2: TESTIMONIAL + LIVE PREVIEW ──── */}
        {step===2&&(
          <div>
            <div style={{background:'linear-gradient(135deg,#1DB954,#16a34a)',padding:'22px 28px'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
                <div>
                  <div style={{color:'rgba(255,255,255,.8)',fontSize:'.63rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:2}}>Step 2 of 2 · Your Testimonial</div>
                  <div style={{color:'#fff',fontWeight:900,fontSize:'1rem'}}>Share your experience</div>
                </div>
                <div style={{display:'flex',gap:2}}>
                  {[1,2,3,4,5].map(n=>(<span key={n} style={{color:n<=stars?'#fff':'rgba(255,255,255,.3)',fontSize:'1.2rem'}}>★</span>))}
                </div>
              </div>
              <ProgressBar step={1}/>
            </div>

            <div style={{padding:'22px 28px'}}>
              {/* Tips */}
              <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:10,padding:'10px 14px',marginBottom:14}}>
                <div style={{fontSize:'.7rem',fontWeight:800,color:'#166534',marginBottom:2}}>💡 Great review tips:</div>
                <div style={{fontSize:'.7rem',color:'#166534',lineHeight:1.65}}>What did you learn? · What made it great? · Would you recommend them?</div>
              </div>

              {/* Textarea */}
              <div style={{position:'relative',marginBottom:6}}>
                <textarea ref={taRef} className="rev-ta" value={text} onChange={e=>setText(e.target.value)}
                  placeholder={`Share your experience with ${session.mentor}...\n\nExample: "Amazing session! ${session.mentor} explained complex concepts clearly and gave me actionable steps. Will definitely book again!"`}
                  rows={5}
                  style={{width:'100%',padding:'13px 14px',border:'1.5px solid #e5e7eb',borderRadius:12,fontFamily:'inherit',fontSize:'.85rem',lineHeight:1.7,resize:'none',color:'#374151',boxSizing:'border-box',transition:'all .2s'}}/>
                <div style={{position:'absolute',bottom:10,right:12,fontSize:'.63rem',fontWeight:700,color:text.length>=MIN_CHARS?'#16a34a':'#9ca3af'}}>
                  {text.length} / {MIN_CHARS}
                </div>
              </div>
              {text.length>0&&text.length<MIN_CHARS&&(
                <div style={{fontSize:'.7rem',color:'#f59e0b',marginBottom:10}}>⚠️ {MIN_CHARS-text.length} more characters needed</div>
              )}

              {/* Tags */}
              <div style={{marginBottom:16}}>
                <div style={{fontSize:'.63rem',fontWeight:800,textTransform:'uppercase',letterSpacing:'.07em',color:'#9ca3af',marginBottom:7}}>Highlight tags (optional)</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                  {TAGS.map(t=>(<button key={t} className={`rev-tag${tags.includes(t)?' sel':''}`} onClick={()=>toggleTag(t)}>{t}</button>))}
                </div>
              </div>

              {/* Live Preview */}
              <div style={{marginBottom:16}}>
                <div style={{fontSize:'.63rem',fontWeight:800,textTransform:'uppercase',letterSpacing:'.07em',color:'#9ca3af',marginBottom:7}}>👁 Live Preview</div>
                <PreviewCard session={session} stars={stars} testimonial={text} tags={tags} isPublic={pub} user={user}/>
              </div>

              {/* Public toggle */}
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'11px 14px',background:'#f9fafb',borderRadius:10,marginBottom:18,border:'1px solid #f3f4f6'}}>
                <div>
                  <div style={{fontWeight:700,fontSize:'.82rem',color:'#374151'}}>{pub?'🌐 Public Review':'🔒 Anonymous Review'}</div>
                  <div style={{fontSize:'.69rem',color:'#9ca3af',marginTop:1}}>{pub?"Your name appears on mentor's profile":'Shows as "Anonymous Mentee"'}</div>
                </div>
                <div onClick={()=>setPub(p=>!p)} style={{width:42,height:22,borderRadius:100,cursor:'pointer',flexShrink:0,background:pub?'#1DB954':'#e5e7eb',position:'relative',transition:'background .2s'}}>
                  <div style={{position:'absolute',top:2,left:pub?21:2,width:18,height:18,borderRadius:'50%',background:'#fff',boxShadow:'0 1px 3px rgba(0,0,0,.25)',transition:'left .2s'}}/>
                </div>
              </div>

              <div style={{display:'flex',gap:10}}>
                <button onClick={()=>setStep(1)} style={{padding:'13px 16px',borderRadius:12,border:'1.5px solid #e5e7eb',background:'#fff',fontFamily:'inherit',color:'#6b7280',cursor:'pointer',fontWeight:600}}>←</button>
                <button onClick={handleSubmit} disabled={!canSubmit||submitting}
                  style={{flex:1,padding:'13px',borderRadius:12,border:'none',fontFamily:'inherit',fontWeight:900,fontSize:'.9rem',cursor:canSubmit?'pointer':'not-allowed',background:canSubmit?'#1DB954':'#e5e7eb',color:canSubmit?'#fff':'#9ca3af',transition:'all .2s'}}>
                  {submitting
                    ?<span style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
                        <span style={{display:'inline-block',width:14,height:14,border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .6s linear infinite'}}/>
                        Submitting…
                      </span>
                    :'🚀 Submit Testimonial'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── STEP 3: THANK YOU ───────────────────── */}
        {step===3&&(
          <div style={{padding:'40px 28px',textAlign:'center',position:'relative',overflow:'hidden'}}>
            {CONFETTI.map((c,i)=>(
              <div key={'a'+i} style={{position:'absolute',top:0,left:`${8+i*11}%`,width:8,height:8,background:c,borderRadius:i%2===0?'50%':2,animation:`confettiFall ${1.1+i*.18}s ease-in ${i*.08}s both`}}/>
            ))}
            {[...CONFETTI,...CONFETTI].map((c,i)=>(
              <div key={'b'+i} style={{position:'absolute',top:0,left:`${3+i*5}%`,width:i%3===0?6:4,height:i%3===0?6:4,background:c,borderRadius:i%3===0?'50%':1,animation:`confettiFall ${1+(i%5)*.2}s ease-in ${(i%7)*.07}s both`}}/>
            ))}

            <div style={{fontSize:68,lineHeight:1,marginBottom:10,animation:'starPop .5s cubic-bezier(.34,1.56,.64,1) .2s both'}}>🏆</div>
            <h2 style={{fontWeight:900,fontSize:'1.5rem',color:'#111827',margin:'0 0 8px',letterSpacing:'-.5px'}}>
              Thank you, {user?.name?.split(' ')[0]||'Rockstar'}!
            </h2>
            <p style={{color:'#6b7280',lineHeight:1.7,marginBottom:4,fontSize:'.88rem'}}>
              Your review is live on <strong>{session.mentor}'s</strong> profile.
            </p>

            {/* Star display */}
            <div style={{display:'flex',justifyContent:'center',gap:3,margin:'14px 0 6px'}}>
              {[1,2,3,4,5].map(n=>(
                <span key={n} style={{fontSize:'1.9rem',color:n<=stars?'#f59e0b':'#e5e7eb',filter:n<=stars?'drop-shadow(0 2px 6px rgba(245,158,11,.45))':'none',animation:n<=stars?`starPop .4s cubic-bezier(.34,1.56,.64,1) ${n*.07}s both`:'none'}}>★</span>
              ))}
            </div>
            <div style={{fontWeight:800,color:STAR_COLORS[stars],fontSize:'.92rem',marginBottom:20}}>{STAR_LABELS[stars]}</div>

            {/* Preview card */}
            <div style={{marginBottom:22,textAlign:'left'}}>
              <PreviewCard session={session} stars={stars} testimonial={text} tags={tags} isPublic={pub} user={user}/>
            </div>

            {/* Share */}
            <div style={{marginBottom:18}}>
              <div style={{fontSize:'.63rem',fontWeight:800,textTransform:'uppercase',letterSpacing:'.07em',color:'#9ca3af',marginBottom:9}}>Share your achievement</div>
              <div style={{display:'flex',gap:8,justifyContent:'center',flexWrap:'wrap'}}>
                <a href={liUrl} target="_blank" rel="noreferrer"
                  style={{display:'inline-flex',alignItems:'center',gap:6,padding:'9px 16px',borderRadius:10,border:'1.5px solid #0A66C2',color:'#0A66C2',textDecoration:'none',fontFamily:'inherit',fontWeight:700,fontSize:'.79rem'}}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  LinkedIn
                </a>
                <a href={twUrl} target="_blank" rel="noreferrer"
                  style={{display:'inline-flex',alignItems:'center',gap:6,padding:'9px 16px',borderRadius:10,border:'1.5px solid #1DA1F2',color:'#1DA1F2',textDecoration:'none',fontFamily:'inherit',fontWeight:700,fontSize:'.79rem'}}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
                  Twitter / X
                </a>
              </div>
            </div>

            <button onClick={onClose} style={{padding:'11px 32px',borderRadius:12,border:'none',background:'#1DB954',color:'#fff',fontFamily:'inherit',fontWeight:800,fontSize:'.9rem',cursor:'pointer'}}>
              🎯 Continue
            </button>
            <p style={{color:'#d1d5db',fontSize:'.64rem',marginTop:10}}>
              {pub?'🌐 Public':'🔒 Anonymous'} · {new Date().toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
